import * as React from "react";
import {NavigationConfiguration} from "../../../../config/navigation-configuration";
import SVG from "react-inlinesvg";
import {autorun} from "mobx";
import {withStore} from "../../../../core/stores/abstract-app-store";
import {MapViewerStore} from "../../../../stores/map-viewer-store-mobx";

export interface FaqProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    store?: MapViewerStore; // this is injected by withStore
}

class FaqBase extends React.Component<FaqProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    constructor(props: FaqProps) {
        super(props);
        this.state = {
            preview: {eingegangen: null,
                vernehmlassung: null,
                entscheidung: null,
                leitentscheid: null,
                abgeschlossen: null}
        };
    }
    componentDidMount(): void {
        const { store, view } = this.props;
        const breadcrumbs = new NavigationConfiguration();
        breadcrumbs.items = [
            { title: "FAQ" }
        ];
        store?.appStore.setBreadcrumbs(breadcrumbs);
        store?.appStore.setPageTitle("Häufig gestellte Fragen");
        store?.appStore.setAsideContent(null);
        store?.oStore.setShowMap(false);
        autorun((reaction: any) => {
            if (!view) {
                return;
            }
            reaction.dispose();
            store?.oStore.loadProjektPreview();
        });
        store?.oStore.loadProjektPreview();
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (store?.oStore.projektpreviewLoaded) {
                this.setState({
                    preview: {
                        eingegangen: store?.oStore.getProjektPreview("Eingegangen"),
                        vernehmlassung: store?.oStore.getProjektPreview("Vernehmlassung"),
                        entscheidung: store?.oStore.getProjektPreview("Entscheidung"),
                        leitentscheid: store?.oStore.getProjektPreview("Leitentscheid"),
                        abgeschlossen: store?.oStore.getProjektPreview("Abgeschlossen")
                    }
                });
                reaction.dispose();
            }
        }));
    }
    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }
    render(): React.ReactElement | null {
        const {preview} = this.state;
        return(
            <div className={"faq"}>
                <ul>
                    <li><h3>Welche Grundstücke sind im Informationssystem objekt.lu abfragbar?</h3>
                        <p>Abgefragt werden können Liegenschaften sowie selbständige und dauernde Rechte
                            (wie Baurechte). Nicht unterstützt werden Grundstücke, die nicht in der amtlichen Vermessung
                            geführt werden, wie z.B. Stockwerkeigentum.</p>
                    </li>
                    <li><h3>Welche Gebäude sind im Informationssystem objekt.lu abfragbar?</h3>
                        <p>Es können sämtliche adressierten Objekte abgefragt werden. Bis Ende 2022 wird das GWR um
                            sämtliche Nicht-Wohngebäude erweitert, welche anschliessend ebenfalls verfügbar sind.</p>
                    </li>
                    <li><h3>Welche Bauprojekte sind im Informationssystem objekt.lu abfragbar?</h3>
                        <p>Gesucht werden können alle Bauprojekte, die seit dem 01.01.2013 in der kantonalen
                            Baugesuchsverwaltung eBAGE+ erfasst wurden. Dabei handelt es sich um jene Baugesuche, bei
                            denen der Kanton am Verfahren beteiligt ist, was für rund 90% aller Gesuche gilt.</p>
                    </li>
                    <li><h3>Was bedeutet die Statusangabe zu einem Bauprojekt?</h3>
                        <dl>
                            <dt className={"geo-status"}>
                                <div className={"esri-symbol"}>
                                    {preview.eingegangen? (<SVG src={preview.eingegangen} />): ""}
                                </div>
                            Eingegangen; Triage; Sichten</dt>
                            <dd>Gesuch ist erfasst und Verfahren wurde gestartet</dd>
                            <dt className={"geo-status"}>
                                <div className={"esri-symbol"}>
                                    {preview.vernehmlassung? (<SVG src={preview.vernehmlassung} />): ""}
                                </div>
                            Vernehmlassung</dt>
                            <dd>Gesuch wird von betroffenen Fachstellen geprüft</dd>
                            <dt className={"geo-status"}>
                                <div className={"esri-symbol"}>
                                    {preview.entscheidung? (<SVG src={preview.entscheidung} />): ""}
                                </div>
                            Entscheidung</dt>
                            <dd>Gesuch wurde abschliessend geprüft und Verfahren wird mittels Entscheid
                                abgeschlossen</dd>
                            <dt className={"geo-status"}>
                                <div className={"esri-symbol"}>
                                    {preview.leitentscheid? (<SVG src={preview.leitentscheid} />): ""}
                                </div>
                            Leitentscheid</dt>
                            <dd>Gesuch ist auf kantonaler Ebene zuhanden der Gemeinde (Leitbehörde) abgeschlossen</dd>
                            <dt className={"geo-status"}>
                                <div className={"esri-symbol"}>
                                    {preview.abgeschlossen? (<SVG src={preview.abgeschlossen} />): ""}
                                </div>
                            Abgeschlossen; Archiviert</dt>
                            <dd>Gesuch ist auf kantonaler und kommunaler Ebene mittels Entscheid abgeschlossen
                                und archiviert</dd>
                        </dl>
                    </li>
                    <li><h3>Warum werden bei der Suche teilweise mehrere Objekte gleichzeitig ausgewiesen?</h3>
                        <p>Die Datenbestände von kGWR, Baugesuchsverwaltung eBAGE+ und amtlicher Vermessung (AV) sind
                            aufgrund nicht vollständig gleichgeschalteter Prozesse nicht zu 100% synchronisiert:</p>
                        <ul>
                            <li>während ein projektiertes Objekt im kGWR und in eBAGE+ ab dem Zeitpunkt der
                                Ersterfassung geführt wird, wird es in der AV erst mit erteilter Baubewilligung als
                                projektiertes Objekt erfasst.</li>
                            <li>eine Baubewilligung ist 2 Jahre gültig und kann verlängert werden, die Nachführung in
                                der AV (als bestehendes Objekt) erfolgt erst nach Bauvollendung.</li>
                        </ul>
                    </li>
                    <li><h3>Wie nutze ich das Informationssystem Objektwesen?</h3>
                        <p className={"richtext"}>
                            <a href={"./files/03_objekt.lu_Benutzeranleitung_Informationssystem_210624.pdf"}
                                className={"pdf"} target={"_blank"}>
                                <span className={"linked"}>Benutzeranleitung Informationssystem</span></a>
                        </p>
                    </li>
                </ul>
            </div>
        );
    }
}

export const Faq = withStore(FaqBase);
